import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useManagedWorkspace } from '../hooks/useManagedWorkspace';
import { apiRequest } from '../lib/queryClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Pagination } from '../components/Pagination';
import { Plus, Trash2 } from 'lucide-react';
import CreateRoleDialog from '../components/roles/CreateRoleDialog';
import { useToast } from '../hooks/use-toast';
import { useWorkspaceRoles } from '../hooks/useWorkspaceRoles';

export default function WorkspaceRoles() {
  const { currentWorkspace } = useWorkspace();
  const { managedWorkspace } = useManagedWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState(10);

  const workspaceId = managedWorkspace?._id;

  const { data: roles = [], isLoading } = useWorkspaceRoles();

  const { data: members = [], isLoading: membersLoading } = useQuery<any[]>({
    queryKey: ['workspace-members', workspaceId],
    queryFn: async () => apiRequest('GET', `/api/workspaces/${workspaceId}/members`) as Promise<any[]>,
    enabled: !!workspaceId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (roleId: string) => {
      await apiRequest('DELETE', `/api/workspaces/${workspaceId}/roles/${roleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-roles'] });
      toast({ title: 'Role deleted successfully' });
      setRoleToDelete(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      setRoleToDelete(null);
    },
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ memberId, roleId }: { memberId: string; roleId: string }) => {
      await apiRequest('PATCH', `/api/workspaces/${workspaceId}/members/${memberId}/role`, { roleId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-members', workspaceId] });
      toast({ title: 'Member role updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  if (isLoading || membersLoading || !managedWorkspace) return <div>Loading...</div>;

  const roleToDeleteName = roles.find((r) => r._id === roleToDelete)?.displayName ?? 'this role';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">Workspace Roles</h1>
          <p className="text-muted-foreground">Manage roles and permissions for your workspace</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Role
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Left: Members */}
        {members.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Members</CardTitle>
              <CardDescription>Assign roles to workspace members</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {members
                  .slice((memberPage - 1) * memberPageSize, memberPage * memberPageSize)
                  .map((member) => (
                    <div key={member._id} className="flex items-center justify-between">
                      <div>
                        {(member.firstName || member.lastName) && (
                          <p className="text-sm font-medium">
                            {[member.firstName, member.lastName].filter(Boolean).join(' ')}
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground">{member.userEmail}</p>
                      </div>
                      <Select
                        value={member.roleId?.toString() || ''}
                        onValueChange={(roleId) =>
                          updateMemberRoleMutation.mutate({ memberId: member._id, roleId })
                        }
                        disabled={updateMemberRoleMutation.isPending}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue placeholder={member.role} />
                        </SelectTrigger>
                        <SelectContent>
                          {roles
                            .filter((r) => r.isSystemRole)
                            .map((role) => (
                              <SelectItem key={role._id} value={role._id}>
                                {role.displayName}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
              </div>
              {members.length > memberPageSize && (
                <div className="mt-4">
                  <Pagination
                    currentPage={memberPage}
                    totalPages={Math.ceil(members.length / memberPageSize)}
                    onPageChange={setMemberPage}
                    totalItems={members.length}
                    itemsPerPage={memberPageSize}
                    onPageSizeChange={(size) => {
                      setMemberPageSize(size);
                      setMemberPage(1);
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Right: Roles */}
        <div className="space-y-4">
          {roles.map((role) => (
            <Card key={role._id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {role.displayName}
                      {role.isSystemRole && <Badge variant="secondary">System</Badge>}
                    </CardTitle>
                    <CardDescription>{role.description}</CardDescription>
                  </div>
                  {!role.isSystemRole && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRoleToDelete(role._id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm">
                  <strong>Permissions:</strong>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {Object.entries(role.permissions).map(([resource, perms]) => (
                      <div key={resource} className="text-xs">
                        <span className="font-medium capitalize">{resource}:</span>{' '}
                        {Object.entries(perms as Record<string, boolean>)
                          .filter(([, allowed]) => allowed)
                          .map(([action]) => action)
                          .join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <CreateRoleDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />

      <AlertDialog open={!!roleToDelete} onOpenChange={() => setRoleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{roleToDeleteName}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Members assigned this role will lose the associated permissions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => roleToDelete && deleteMutation.mutate(roleToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
